import { FileText, SearchIcon } from "lucide-react";
import { useState } from "react";
import { useSearchParams } from "react-router";

import { EstadoDeError } from "@/components/estados/EstadoDeError";
import { EstadoSinConexion } from "@/components/estados/EstadoSinConexion";
import { ContactoDialog } from "@/components/informes/ContactoDialog";
import { FilaDeInformeEnProceso } from "@/components/informes/FilaDeInformeEnProceso";
import { ModalDeAceptacionManual } from "@/components/informes/ModalDeAceptacionManual";
import { ChipDeAtencion } from "@/components/ui/chip-de-atencion";
import { Empty, EmptyDescription, EmptyHeader, EmptyMedia, EmptyTitle } from "@/components/ui/empty";
import { FiltroDePersona } from "@/components/ui/filtro-de-persona";
import { FiltroSegmentado } from "@/components/ui/filtro-segmentado";
import { Input } from "@/components/ui/input";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";
import { SelectSinBorde } from "@/components/ui/select-sin-borde";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useAccionDemorada } from "@/hooks/useAccionDemorada";
import { useAuth } from "@/hooks/useAuth";
import { useCatalogosDelInforme, useInformesEnProceso } from "@/hooks/useInformes";
import { estadoDeConsulta } from "@/lib/estadoDeConsulta";
import type {
  AtencionDeInformes,
  OrdenDeInformes,
  RolDeFiltro,
  TabDeInformes,
} from "@/lib/informes";

const FILAS_SKELETON = 5;

const TABS: { valor: TabDeInformes; etiqueta: string }[] = [
  { valor: "activos", etiqueta: "Activos" },
  { valor: "creados", etiqueta: "Creados" },
  { valor: "visado", etiqueta: "En visado" },
  { valor: "enviados", etiqueta: "Enviados" },
  { valor: "todos", etiqueta: "Todos" },
];

const ROLES: { valor: RolDeFiltro; etiqueta: string }[] = [
  { valor: "cualquiera", etiqueta: "en cualquier rol" },
  { valor: "le_toca", etiqueta: "a quien le toca ahora" },
  { valor: "ejecutivo", etiqueta: "como ejecutivo" },
  { valor: "encargado_terreno", etiqueta: "como encargado de terreno" },
  { valor: "revisor_cotizacion", etiqueta: "como revisor de cotización" },
  { valor: "revisor_informe", etiqueta: "como revisor del informe final" },
];

const ATENCIONES: { valor: AtencionDeInformes; etiqueta: string; tono: "danger" | "info" | "warning" }[] = [
  { valor: "atrasados", etiqueta: "Atrasados", tono: "danger" },
  { valor: "novedades_app", etiqueta: "Novedades de la app", tono: "info" },
  { valor: "dudas_ia", etiqueta: "Dudas de la IA", tono: "warning" },
  { valor: "anticipo_pendiente", etiqueta: "Anticipo pendiente", tono: "warning" },
];

const ORDENES: { valor: OrdenDeInformes; etiqueta: string }[] = [
  { valor: "fecha", etiqueta: "fecha de creación" },
  { valor: "entrega", etiqueta: "fecha de entrega" },
  { valor: "actividad", etiqueta: "última actividad" },
];

/** Lo que dice la URL, validado: un valor inventado cae al defecto. */
function enLista<V extends string>(valor: string | null, lista: { valor: V }[], defecto: V): V {
  return lista.find((item) => item.valor === valor)?.valor ?? defecto;
}

/**
 * Informes en proceso: la cola de trabajo, vista por etapa, persona y atención.
 *
 * El backend filtra, ordena, cuenta y pagina; el front solo traduce los
 * controles a parámetros. Todo vive en la URL —y no en `useState`— para que
 * recargar, compartir el enlace o volver con "atrás" conserven lo elegido.
 *
 * El título y la miga viven en el `TopBar`; el "Crear informe" vive en el acceso
 * rápido del menú.
 */
export function InformesEnProceso() {
  const [params, setParams] = useSearchParams();

  const tab = enLista(params.get("tab"), TABS, "activos");
  const atencion = ATENCIONES.find((a) => a.valor === params.get("atencion"))?.valor;
  const orden = enLista(params.get("orden"), ORDENES, "fecha");
  const personaId = Number(params.get("persona")) || undefined;
  const rol = enLista(params.get("rol"), ROLES, "cualquiera");
  const q = params.get("q") ?? "";
  const pagina = Number(params.get("pagina")) || 1;

  const [contactoId, setContactoId] = useState<number | null>(null);
  const [aceptacionId, setAceptacionId] = useState<number | null>(null);
  const [pagoDelAbonoId, setPagoDelAbonoId] = useState<number | null>(null);

  // El texto del buscador es local (se teclea a la velocidad de la mano) y baja
  // a la URL cuando se deja de teclear. Si la URL cambia por otro camino —
  // "Limpiar filtros", el botón "atrás"— el texto la sigue.
  const [texto, setTexto] = useState(q);
  const [qPrevia, setQPrevia] = useState(q);
  if (q !== qPrevia) {
    setQPrevia(q);
    setTexto(q);
  }

  const listado = useInformesEnProceso({ q, tab, personaId, rol, atencion, orden, pagina });
  const catalogos = useCatalogosDelInforme();
  const { user } = useAuth();
  const estadoConsulta = estadoDeConsulta(listado);

  /** Cambiar un filtro vuelve SIEMPRE a la página 1. */
  function cambiarFiltro(cambios: Record<string, string | null>) {
    const siguiente = new URLSearchParams(params);
    for (const [clave, valor] of Object.entries(cambios)) {
      if (valor) siguiente.set(clave, valor);
      else siguiente.delete(clave);
    }
    siguiente.delete("pagina");
    setParams(siguiente);
  }

  const buscar = useAccionDemorada((valor: string) => cambiarFiltro({ q: valor.trim() }));

  const hayFiltros = q !== "" || tab !== "activos" || personaId !== undefined || atencion !== undefined;

  function limpiar() {
    const siguiente = new URLSearchParams(params);
    for (const clave of ["q", "tab", "persona", "rol", "atencion", "pagina"]) siguiente.delete(clave);
    setTexto("");
    setParams(siguiente);
  }

  function urlDePagina(n: number): string {
    const siguiente = new URLSearchParams(params);
    if (n > 1) siguiente.set("pagina", String(n));
    else siguiente.delete("pagina");
    return `?${siguiente.toString()}`;
  }

  function irAPagina(evento: React.MouseEvent, n: number) {
    // Enlaces REALES (con href) para el teclado y el clic con rueda; el
    // `preventDefault` deja la navegación del lado del cliente.
    evento.preventDefault();
    setParams(new URLSearchParams(urlDePagina(n)));
  }

  // Las personas del selector: "Yo" primero (con el id de la sesión) y después el
  // resto de los usuarios asignables, sin repetir a quien ya es "Yo".
  const personas = [
    ...(user ? [{ valor: String(user.id), etiqueta: "Yo" }] : []),
    ...(catalogos.data?.usuarios ?? [])
      .filter((usuario) => usuario.id !== user?.id)
      .map((usuario) => ({ valor: String(usuario.id), etiqueta: usuario.nombre })),
  ];

  const informes = listado.data?.informes ?? [];
  const informeDelContacto = informes.find((informe) => informe.id === contactoId) ?? null;
  const informeAAceptar = informes.find((informe) => informe.id === aceptacionId) ?? null;

  const total = listado.data?.total ?? 0;
  const conteos = listado.data?.conteos;
  const sinNada = !estadoConsulta.cargando && total === 0 && !hayFiltros;
  const sinCoincidencias = !estadoConsulta.cargando && total === 0 && hayFiltros;

  return (
    <div className="space-y-4 px-6 pt-2 pb-6 lg:px-8 lg:pb-8">
      <div className="flex justify-end">
        <div className="relative w-[21.25rem] max-w-full">
          <SearchIcon
            aria-hidden
            className="text-ink-medio pointer-events-none absolute top-1/2 left-3.5 size-[0.9375rem] -translate-y-1/2"
          />
          <Input
            type="search"
            aria-label="Buscar informes"
            placeholder="Buscar predio, N° de informe o cliente"
            value={texto}
            onChange={(evento) => {
              setTexto(evento.target.value);
              buscar(evento.target.value);
            }}
            className="pl-9 text-[0.8125rem] font-medium"
          />
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <FiltroSegmentado
          aria-label="Filtrar por etapa"
          valor={tab}
          onCambio={(valor) => cambiarFiltro({ tab: valor === "activos" ? null : valor })}
          opciones={TABS.map(({ valor, etiqueta }) => ({
            valor,
            etiqueta,
            cantidad: conteos?.tabs[valor],
          }))}
        />
        <FiltroDePersona
          className="ml-auto"
          personas={personas}
          persona={personaId ? String(personaId) : ""}
          onPersona={(valor) => cambiarFiltro({ persona: valor || null, rol: valor ? (params.get("rol") ?? null) : null })}
          roles={ROLES}
          rol={rol}
          onRol={(valor) => cambiarFiltro({ rol: valor === "cualquiera" ? null : valor })}
        />
      </div>

      <div className="flex flex-wrap items-center gap-2">
        {ATENCIONES.map(({ valor, etiqueta, tono }) => (
          <ChipDeAtencion
            key={valor}
            tono={tono}
            activo={atencion === valor}
            cantidad={conteos?.atencion[valor] ?? 0}
            onClick={() => cambiarFiltro({ atencion: atencion === valor ? null : valor })}
          >
            {etiqueta}
          </ChipDeAtencion>
        ))}
        {hayFiltros && (
          <button
            type="button"
            onClick={limpiar}
            className="text-ink-muted hover:text-ink rounded-md px-1.5 text-xs font-bold"
          >
            Limpiar filtros
          </button>
        )}
        <div className="ml-auto flex items-center gap-1">
          <label htmlFor="orden-de-informes" className="text-ink-medio text-xs font-semibold">
            Ordenar por
          </label>
          <SelectSinBorde
            id="orden-de-informes"
            value={orden}
            onChange={(evento) =>
              cambiarFiltro({ orden: evento.target.value === "fecha" ? null : evento.target.value })
            }
          >
            {ORDENES.map(({ valor, etiqueta }) => (
              <option key={valor} value={valor}>
                {etiqueta}
              </option>
            ))}
          </SelectSinBorde>
        </div>
      </div>

      {estadoConsulta.hayError ? (
        <EstadoDeError
          titulo="No pudimos cargar los informes"
          error={listado.error}
          onReintentar={() => listado.refetch()}
          reintentando={estadoConsulta.recargando}
        />
      ) : estadoConsulta.sinConexion ? (
        <EstadoSinConexion que="los informes" />
      ) : sinNada ? (
        <Empty className="rounded-xl border">
          <EmptyHeader>
            <EmptyMedia variant="icon">
              <FileText />
            </EmptyMedia>
            <EmptyTitle>No hay informes que mostrar</EmptyTitle>
            <EmptyDescription>
              Cuando se cree el primer informe de tasación va a aparecer aquí.
            </EmptyDescription>
          </EmptyHeader>
        </Empty>
      ) : (
        <>
          <section className="border-border overflow-hidden rounded-[1.125rem] border-[1.5px]">
            <Table className="min-w-[64rem] table-fixed">
              <TableHeader className="bg-superficie-tenue [&_tr]:border-divisor-fuerte">
                <TableRow className="hover:bg-transparent">
                  <TableHead variant="versalitas" className="w-[27%] pl-[1.125rem]">
                    Predio · N° informe
                  </TableHead>
                  <TableHead variant="versalitas" className="w-[19%]">
                    Cliente
                  </TableHead>
                  <TableHead variant="versalitas" className="w-[12.5rem]">
                    Etapa
                  </TableHead>
                  <TableHead variant="versalitas">Pendientes</TableHead>
                  <TableHead variant="versalitas" className="w-[8.5rem]">
                    Entrega
                  </TableHead>
                  <TableHead variant="versalitas" className="w-[6.5rem]">
                    Equipo
                  </TableHead>
                  <TableHead variant="versalitas" className="w-14">
                    <span className="sr-only">Acciones</span>
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {estadoConsulta.cargando
                  ? Array.from({ length: FILAS_SKELETON }).map((_, i) => (
                      <TableRow key={i} aria-label="Cargando informe" className="h-16 hover:bg-transparent">
                        <TableCell className="pl-[1.125rem]">
                          <Skeleton className="h-3.5 w-48" />
                          <Skeleton className="mt-1.5 h-2.5 w-32" />
                        </TableCell>
                        <TableCell>
                          <Skeleton className="h-3.5 w-36" />
                        </TableCell>
                        <TableCell>
                          <Skeleton className="h-1.5 w-full" />
                          <Skeleton className="mt-2 h-3 w-24" />
                        </TableCell>
                        <TableCell>
                          <Skeleton className="h-6 w-28 rounded-md" />
                        </TableCell>
                        <TableCell>
                          <Skeleton className="h-3.5 w-20" />
                          <Skeleton className="mt-1.5 h-2.5 w-14" />
                        </TableCell>
                        <TableCell>
                          <Skeleton className="size-[1.875rem] rounded-full" />
                        </TableCell>
                        <TableCell>
                          <Skeleton className="size-8 rounded-md" />
                        </TableCell>
                      </TableRow>
                    ))
                  : informes.map((informe) => (
                      <FilaDeInformeEnProceso
                        key={informe.id}
                        informe={informe}
                        pagoAbierto={pagoDelAbonoId === informe.id}
                        onAbrirPago={() => setPagoDelAbonoId(informe.id)}
                        onCerrarPago={() => setPagoDelAbonoId(null)}
                        onVerContacto={() => setContactoId(informe.id)}
                        onMarcarAceptada={() => setAceptacionId(informe.id)}
                      />
                    ))}
              </TableBody>
            </Table>

            {sinCoincidencias && (
              <div className="px-[1.125rem] py-12 text-center">
                <p className="text-ink text-sm font-bold">Ningún informe coincide</p>
                <p className="text-ink-muted mt-1 text-[0.78125rem] font-semibold">
                  Prueba con otra búsqueda o{" "}
                  <button
                    type="button"
                    onClick={limpiar}
                    className="text-success-soft-foreground font-bold hover:underline"
                  >
                    limpia los filtros
                  </button>
                  .
                </p>
              </div>
            )}
          </section>

          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="text-ink-medio text-xs font-semibold">
              Mostrando {total} de {listado.data?.totalGeneral ?? 0} informes
            </p>

            {(listado.data?.totalPaginas ?? 1) > 1 && (
              <Pagination className="mx-0 w-auto justify-end">
                <PaginationContent>
                  <PaginationItem>
                    <PaginationPrevious
                      href={urlDePagina(pagina - 1)}
                      aria-label="Página anterior"
                      aria-disabled={pagina === 1}
                      className={pagina === 1 ? "pointer-events-none opacity-50" : undefined}
                      onClick={(evento) => irAPagina(evento, pagina - 1)}
                    />
                  </PaginationItem>

                  {Array.from({ length: listado.data?.totalPaginas ?? 1 }, (_, i) => i + 1).map((n) => (
                    <PaginationItem key={n}>
                      <PaginationLink
                        href={urlDePagina(n)}
                        isActive={n === pagina}
                        onClick={(evento) => irAPagina(evento, n)}
                      >
                        {n}
                      </PaginationLink>
                    </PaginationItem>
                  ))}

                  <PaginationItem>
                    <PaginationNext
                      href={urlDePagina(pagina + 1)}
                      aria-label="Página siguiente"
                      aria-disabled={pagina === (listado.data?.totalPaginas ?? 1)}
                      className={
                        pagina === (listado.data?.totalPaginas ?? 1)
                          ? "pointer-events-none opacity-50"
                          : undefined
                      }
                      onClick={(evento) => irAPagina(evento, pagina + 1)}
                    />
                  </PaginationItem>
                </PaginationContent>
              </Pagination>
            )}
          </div>
        </>
      )}

      <ContactoDialog informe={informeDelContacto} onCerrar={() => setContactoId(null)} />

      {/* Montada solo con informe elegido y con `key` por id: el formulario nace
          limpio para cada uno en vez de heredar el motivo de otro cliente. */}
      {informeAAceptar && (
        <ModalDeAceptacionManual
          key={informeAAceptar.id}
          informeId={informeAAceptar.id}
          propiedad={informeAAceptar.propiedad_nombre}
          abierto
          onCerrar={() => setAceptacionId(null)}
        />
      )}
    </div>
  );
}
